/**
 * TrustedContactEditModal — small modal for filling in the three optional
 * fields on a Trusted Contact (phone, address, notes) without touching the
 * required identity fields.
 *
 * Opened from the amber "Add {label}" nudge chips on each Trusted Contact
 * row. Saves via PATCH /api/estate-vault/successors/{id} through the
 * useEstateVaultActions.handleUpdateTrustedContact handler.
 */
import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const pick = (c) => ({
  phone: c?.phone || '',
  address: c?.address || '',
  relationship_notes: c?.relationship_notes || '',
});

export function TrustedContactEditModal({ contact, open, onOpenChange, onSave }) {
  const [values, setValues] = useState(() => pick(contact));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setValues(pick(contact)); }, [open, contact]);

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!contact) return;
    setSaving(true);
    const ok = await onSave(contact.successor_id, values);
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="tc-edit-modal">
        <DialogHeader>
          <DialogTitle>Update Trusted Contact</DialogTitle>
          <DialogDescription>
            Fill in the details for {contact?.full_name || 'this Trusted Contact'}.
            None of these are required, but each one helps our admin team reach them if the time ever comes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="tc-edit-phone">Their phone</Label>
            <Input
              id="tc-edit-phone"
              value={values.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="e.g. +44 20 7946 0958"
              data-testid="tc-edit-phone"
            />
          </div>
          <div>
            <Label htmlFor="tc-edit-address">Their address</Label>
            <Input
              id="tc-edit-address"
              value={values.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="e.g. 10 Downing Street, London"
              data-testid="tc-edit-address"
            />
          </div>
          <div>
            <Label htmlFor="tc-edit-notes">Anything our admin team should know</Label>
            <Textarea
              id="tc-edit-notes"
              rows={3}
              value={values.relationship_notes}
              onChange={(e) => set('relationship_notes', e.target.value)}
              placeholder="e.g. preferred phone hours, language, or how they know you"
              data-testid="tc-edit-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tc-edit-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="tc-edit-save">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TrustedContactEditModal;
